import { defineRouting } from 'next-intl/routing';

export const routing = defineRouting({
  locales: ['en-AU', 'zh-TW', 'ja-JP', 'fr-FR'],
  defaultLocale: 'en-AU',
  localePrefix: 'always',
});

export type AppLocale = (typeof routing.locales)[number];

/**
 * Return the locale a path is explicitly prefixed with (e.g. `/ja-JP/event/…`),
 * or `null` when the path carries none (e.g. `/dashboard`). Unlike
 * `extractLocaleFromUrl`, this does NOT fall back to the default locale — so
 * callers can distinguish "no locale in path" from "path is default-locale" and
 * fall through to another source (cookie, referer) before defaulting.
 */
export function matchLocalePrefix(path: string): AppLocale | null {
  for (const locale of routing.locales) {
    if (path === `/${locale}` || path.startsWith(`/${locale}/`)) {
      return locale;
    }
  }
  return null;
}

/**
 * Narrow an untrusted string to a known locale, or `null`.
 *
 * Use this on ANY locale that came from a URL, a cookie, or a header before it
 * reaches a redirect path. A raw `?locale=` interpolated into
 * `new URL(`/${locale}/login`, origin)` is an open redirect: a leading slash
 * makes the result protocol-relative, so `?locale=/evil.com` yields
 * `https://evil.com/login`. Both invite callbacks shipped that bug.
 */
export function asAppLocale(value: string | null | undefined): AppLocale | null {
  return value && (routing.locales as readonly string[]).includes(value)
    ? (value as AppLocale)
    : null;
}

/** Extract locale from a URL path or referer header. Falls back to defaultLocale. */
export function extractLocaleFromUrl(url: string): AppLocale {
  for (const locale of routing.locales) {
    if (url.includes(`/${locale}/`) || url.endsWith(`/${locale}`)) {
      return locale;
    }
  }
  return routing.defaultLocale;
}

/** Extract locale from a request's referer header. */
export function extractLocaleFromReferer(req: {
  headers: { get(name: string): string | null };
}): AppLocale {
  return extractLocaleFromUrl(req.headers.get('referer') ?? '');
}
